import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", code: "", description: "", image: "", seo_title: "", seo_description: "", status: "active" };

export default function AdminCountries() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/countries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/countries/${editing.id}`, form);
      else await api.post("/admin/countries", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete country?")) { await api.delete(`/admin/countries/${id}`); load(); } };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Countries</h1>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-country-btn"><Plus className="mr-1.5 h-4 w-4" />Add Country</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[500px] text-left text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="p-3">Name</th><th className="p-3">Code</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="p-3 font-medium text-slate-900">{c.name}</td>
                <td className="p-3 text-slate-500">{c.code}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${c.status === "active" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span></td>
                <td className="p-3"><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Country</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="country-name" /></div>
            <div><Label>Country Code</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div><Label>Image URL</Label><Input value={form.image} onChange={(e) => set("image", e.target.value)} /></div>
            <div><Label>SEO Title</Label><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></div>
            <div><Label>SEO Description</Label><Input value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.status === "active"} onCheckedChange={(v) => set("status", v ? "active" : "inactive")} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="save-country-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
